import { brandingSchema, type Branding } from '../schemas/branding.js';
import { formSchemaSchema, type FormSchema } from '../schemas/form-schema.js';
import { STANDARD_LAYOUT_CSS, STANDARD_LAYOUT_HTML } from './layout.js';

/**
 * Mitgelieferte System-Templates (SPEC Kap. 5.4, PHASENPLAN 3.9).
 *
 * Sie sind schreibgeschützt und werden zum Anpassen kopiert — niemand baut ein
 * Protokoll von null. Die Schlüssel sind stabil, damit ein erneuter Seed-Lauf
 * keine Duplikate erzeugt.
 */

export interface SystemTemplate {
  schluessel: string;
  name: string;
  art: string;
  beschreibung: string;
  formSchema: FormSchema;
  layoutHtml: string;
  layoutCss: string;
  branding: Branding;
}

const ZUSTAND = ['neuwertig', 'gut', 'gebraucht', 'renovierungsbedürftig', 'defekt'];
const BODEN = ['Parkett', 'Laminat', 'Fliesen', 'Teppich', 'Estrich', 'Vinyl', 'Dielen'];
const WAND = ['gestrichen', 'tapeziert', 'gefliest', 'Rauputz', 'Sichtbeton'];

const allgemeinerAbschnitt = (mitWitterung = true) => ({
  id: 'allgemein',
  titel: 'Allgemeine Angaben',
  felder: [
    {
      id: 'uebergabeDatum',
      typ: 'datum',
      label: 'Übergabe am',
      pflicht: true,
      vorbelegung: 'heute',
      breite: 'halb',
    },
    { id: 'uhrzeit', typ: 'uhrzeit', label: 'Uhrzeit', vorbelegung: 'jetzt', breite: 'halb' },
    ...(mitWitterung
      ? [
          {
            id: 'wetter',
            typ: 'auswahl',
            label: 'Witterung',
            optionen: ['trocken', 'Regen', 'Frost', 'Schnee'],
            breite: 'halb',
          },
        ]
      : []),
    {
      id: 'anwesende',
      typ: 'mehrzeilig',
      label: 'Anwesende Personen',
      hilfetext: 'Wer war bei der Übergabe vor Ort?',
      zeilen: 2,
    },
  ],
});

const raumAbschnitt = (mitDelta: boolean) => ({
  id: 'raeume',
  titel: 'Räume',
  typ: 'wiederholbar',
  quelle: 'objekt.raeume',
  titelFeld: 'name',
  hinzufuegenLabel: 'Raum hinzufügen',
  min: 1,
  felder: [
    { id: 'name', typ: 'text', label: 'Raum', pflicht: true, breite: 'halb' },
    {
      id: 'flaeche',
      typ: 'zahl',
      label: 'Fläche',
      einheit: 'm²',
      dezimalstellen: 2,
      breite: 'halb',
    },
    { id: 'boden', typ: 'auswahl', label: 'Boden', optionen: BODEN, breite: 'drittel' },
    { id: 'wand', typ: 'auswahl', label: 'Wände', optionen: WAND, breite: 'drittel' },
    { id: 'zustand', typ: 'zustandsskala', label: 'Zustand', stufen: ZUSTAND, pflicht: true },
    {
      id: 'renovierungsbedarf',
      typ: 'mehrzeilig',
      label: 'Renovierungsbedarf',
      zeilen: 2,
      sichtbarWenn: { feld: 'zustand', operator: 'gleich', wert: 'renovierungsbedürftig' },
    },
    {
      id: 'maengel',
      typ: 'maengelblock',
      label: 'Mängel',
      fotosProMangelMax: 5,
      kostenschaetzung: mitDelta,
    },
    { id: 'fotos', typ: 'fotos', label: 'Fotos des Raums', max: 10 },
    { id: 'bemerkung', typ: 'mehrzeilig', label: 'Bemerkung', zeilen: 2 },
  ],
});

const zaehlerAbschnitt = {
  id: 'zaehler',
  titel: 'Zählerstände',
  typ: 'wiederholbar',
  titelFeld: 'art',
  hinzufuegenLabel: 'Zähler hinzufügen',
  felder: [
    {
      id: 'art',
      typ: 'auswahl',
      label: 'Zählerart',
      optionen: ['Strom', 'Gas', 'Wasser kalt', 'Wasser warm', 'Heizung'],
      pflicht: true,
      breite: 'drittel',
    },
    { id: 'nummer', typ: 'text', label: 'Zählernummer', breite: 'drittel' },
    {
      id: 'stand',
      typ: 'zahl',
      label: 'Zählerstand',
      dezimalstellen: 2,
      pflicht: true,
      breite: 'drittel',
    },
    { id: 'foto', typ: 'fotos', label: 'Foto des Zählers', max: 2 },
  ],
};

const schluesselAbschnitt = {
  id: 'schluessel',
  titel: 'Schlüssel',
  typ: 'schluesseltabelle',
  beschreibung: 'Bestand laut Stammdaten gegen tatsächlich übergebene Menge.',
};

const unterschriftenAbschnitt = (rollen: string[]) => ({
  id: 'unterschriften',
  titel: 'Unterschriften',
  typ: 'signaturblock',
  rollen,
  mindestens: Math.min(2, rollen.length),
  seitenumbruchVor: false,
});

const branding = (): Branding => brandingSchema.parse({});

const EINZUG: SystemTemplate = {
  schluessel: 'system-wohnung-einzug',
  name: 'Wohnung — Einzug',
  art: 'Einzug',
  beschreibung:
    'Übergabe einer Wohnung an neue Mieter. Räume, Zustand, Zähler, Schlüssel und Mängel.',
  layoutHtml: STANDARD_LAYOUT_HTML,
  layoutCss: STANDARD_LAYOUT_CSS,
  branding: branding(),
  formSchema: formSchemaSchema.parse({
    version: 1,
    titel: 'Wohnungsübergabe — Einzug',
    abschnitte: [
      allgemeinerAbschnitt(),
      raumAbschnitt(false),
      zaehlerAbschnitt,
      schluesselAbschnitt,
      {
        id: 'abschluss',
        titel: 'Abschluss',
        felder: [
          {
            id: 'reinigungszustand',
            typ: 'zustandsskala',
            label: 'Reinigungszustand gesamt',
            stufen: ['besenrein', 'gereinigt', 'unzureichend'],
          },
          {
            id: 'wohnungUebergeben',
            typ: 'jaNein',
            label: 'Wohnung wurde übergeben',
            pflicht: true,
            breite: 'halb',
          },
          {
            id: 'ablehnungsgrund',
            typ: 'mehrzeilig',
            label: 'Grund der Nichtübergabe',
            zeilen: 3,
            pflicht: true,
            sichtbarWenn: { feld: 'wohnungUebergeben', operator: 'gleich', wert: false },
          },
          { id: 'sonstiges', typ: 'mehrzeilig', label: 'Sonstige Vereinbarungen', zeilen: 3 },
        ],
      },
      unterschriftenAbschnitt(['Mieter', 'Vermieter']),
    ],
  }),
};

const AUSZUG: SystemTemplate = {
  schluessel: 'system-wohnung-auszug',
  name: 'Wohnung — Auszug',
  art: 'Auszug',
  beschreibung:
    'Rückgabe einer Wohnung. Zeigt die Werte des Einzugsprotokolls zum Vergleich und erfasst Kostenschätzungen.',
  layoutHtml: STANDARD_LAYOUT_HTML,
  layoutCss: STANDARD_LAYOUT_CSS,
  branding: branding(),
  formSchema: formSchemaSchema.parse({
    version: 1,
    titel: 'Wohnungsübergabe — Auszug',
    abschnitte: [
      allgemeinerAbschnitt(),
      raumAbschnitt(true),
      zaehlerAbschnitt,
      schluesselAbschnitt,
      {
        id: 'abrechnung',
        titel: 'Abrechnung und Kaution',
        felder: [
          {
            id: 'reinigungszustand',
            typ: 'zustandsskala',
            label: 'Reinigungszustand gesamt',
            stufen: ['besenrein', 'gereinigt', 'unzureichend'],
            pflicht: true,
          },
          {
            id: 'schoenheitsreparaturen',
            typ: 'jaNein',
            label: 'Schönheitsreparaturen erforderlich',
            breite: 'halb',
          },
          {
            id: 'geschaetzteKosten',
            typ: 'waehrung',
            label: 'Geschätzte Instandsetzungskosten',
            min: 0,
            breite: 'halb',
            sichtbarWenn: { feld: 'schoenheitsreparaturen', operator: 'gleich', wert: true },
          },
          {
            id: 'kautionEinbehalt',
            typ: 'waehrung',
            label: 'Vorläufiger Kautionseinbehalt',
            min: 0,
            breite: 'halb',
          },
          {
            id: 'neueAnschrift',
            typ: 'text',
            label: 'Neue Anschrift des Mieters',
            hilfetext: 'Für die Betriebskostenabrechnung.',
          },
          { id: 'sonstiges', typ: 'mehrzeilig', label: 'Sonstige Vereinbarungen', zeilen: 3 },
        ],
      },
      unterschriftenAbschnitt(['Mieter', 'Vermieter']),
    ],
  }),
};

const GEWERBE: SystemTemplate = {
  schluessel: 'system-gewerbe',
  name: 'Gewerbe / Lager',
  art: 'Gewerbe',
  beschreibung:
    'Übergabe von Gewerbe- und Lagerflächen mit technischer Ausstattung, Brandschutz und Zugangsmitteln.',
  layoutHtml: STANDARD_LAYOUT_HTML,
  layoutCss: STANDARD_LAYOUT_CSS,
  branding: branding(),
  formSchema: formSchemaSchema.parse({
    version: 1,
    titel: 'Gewerbeübergabe',
    abschnitte: [
      {
        ...allgemeinerAbschnitt(),
        felder: [
          ...allgemeinerAbschnitt().felder,
          { id: 'nutzungsart', typ: 'text', label: 'Vereinbarte Nutzungsart', breite: 'halb' },
        ],
      },
      {
        id: 'flaechen',
        titel: 'Flächen',
        typ: 'wiederholbar',
        quelle: 'objekt.raeume',
        titelFeld: 'name',
        hinzufuegenLabel: 'Fläche hinzufügen',
        min: 1,
        felder: [
          { id: 'name', typ: 'text', label: 'Bezeichnung', pflicht: true, breite: 'halb' },
          {
            id: 'flaeche',
            typ: 'zahl',
            label: 'Fläche',
            einheit: 'm²',
            dezimalstellen: 2,
            breite: 'halb',
          },
          {
            id: 'nutzung',
            typ: 'auswahl',
            label: 'Nutzung',
            optionen: ['Verkauf', 'Büro', 'Lager', 'Produktion', 'Sozialraum', 'Verkehrsfläche'],
            breite: 'drittel',
          },
          {
            id: 'bodenbelastung',
            typ: 'zahl',
            label: 'Zul. Bodenbelastung',
            einheit: 'kg/m²',
            breite: 'drittel',
          },
          { id: 'zustand', typ: 'zustandsskala', label: 'Zustand', stufen: ZUSTAND, pflicht: true },
          { id: 'maengel', typ: 'maengelblock', label: 'Mängel', kostenschaetzung: true },
          { id: 'fotos', typ: 'fotos', label: 'Fotos', max: 10 },
        ],
      },
      {
        id: 'technik',
        titel: 'Technik und Brandschutz',
        felder: [
          {
            id: 'heizungsart',
            typ: 'auswahl',
            label: 'Heizung',
            optionen: ['Fernwärme', 'Gas', 'Öl', 'Wärmepumpe', 'Elektro', 'keine'],
            breite: 'drittel',
          },
          {
            id: 'lueftung',
            typ: 'jaNein',
            label: 'Lüftungsanlage vorhanden',
            breite: 'drittel',
          },
          {
            id: 'starkstrom',
            typ: 'jaNein',
            label: 'Starkstromanschluss',
            breite: 'drittel',
          },
          {
            id: 'brandschutzeinrichtungen',
            typ: 'mehrfachauswahl',
            label: 'Brandschutzeinrichtungen',
            optionen: [
              'Feuerlöscher',
              'Rauchmelder',
              'Brandmeldeanlage',
              'Sprinkler',
              'Notausgangsbeschilderung',
              'Fluchtwegplan',
            ],
          },
          {
            id: 'letztePruefung',
            typ: 'datum',
            label: 'Letzte Brandschutzprüfung',
            breite: 'halb',
          },
          {
            id: 'technikBemerkung',
            typ: 'mehrzeilig',
            label: 'Bemerkungen zur Technik',
            zeilen: 3,
          },
        ],
      },
      zaehlerAbschnitt,
      schluesselAbschnitt,
      {
        id: 'abschluss',
        titel: 'Abschluss',
        felder: [
          {
            id: 'flaecheUebergeben',
            typ: 'jaNein',
            label: 'Fläche wurde übergeben',
            pflicht: true,
          },
          { id: 'sonstiges', typ: 'mehrzeilig', label: 'Sonstige Vereinbarungen', zeilen: 3 },
        ],
      },
      unterschriftenAbschnitt(['Mieter', 'Vermieter', 'Zeuge']),
    ],
  }),
};

const ABNAHME: SystemTemplate = {
  schluessel: 'system-abnahme',
  name: 'Zwischen- / Handwerkerabnahme',
  art: 'Abnahme',
  beschreibung:
    'Abnahme ausgeführter Arbeiten. Mängel werden mit Status geführt und lassen sich über mehrere Termine verfolgen.',
  layoutHtml: STANDARD_LAYOUT_HTML,
  layoutCss: STANDARD_LAYOUT_CSS,
  branding: branding(),
  formSchema: formSchemaSchema.parse({
    version: 1,
    titel: 'Abnahmeprotokoll',
    abschnitte: [
      {
        id: 'allgemein',
        titel: 'Allgemeine Angaben',
        felder: [
          {
            id: 'uebergabeDatum',
            typ: 'datum',
            label: 'Abnahme am',
            pflicht: true,
            vorbelegung: 'heute',
            breite: 'halb',
          },
          { id: 'uhrzeit', typ: 'uhrzeit', label: 'Uhrzeit', vorbelegung: 'jetzt', breite: 'halb' },
          { id: 'auftrag', typ: 'text', label: 'Auftrag / Bestellnummer', breite: 'halb' },
          { id: 'gewerk', typ: 'text', label: 'Gewerk', pflicht: true, breite: 'halb' },
          { id: 'ausfuehrendeFirma', typ: 'text', label: 'Ausführende Firma', pflicht: true },
          {
            id: 'leistungsbeschreibung',
            typ: 'mehrzeilig',
            label: 'Beschreibung der Leistung',
            zeilen: 3,
          },
        ],
      },
      {
        id: 'bereiche',
        titel: 'Abgenommene Bereiche',
        typ: 'wiederholbar',
        quelle: 'objekt.raeume',
        titelFeld: 'name',
        hinzufuegenLabel: 'Bereich hinzufügen',
        min: 1,
        felder: [
          { id: 'name', typ: 'text', label: 'Bereich', pflicht: true, breite: 'halb' },
          {
            id: 'ausfuehrung',
            typ: 'zustandsskala',
            label: 'Ausführung',
            stufen: ['einwandfrei', 'geringe Mängel', 'wesentliche Mängel'],
            pflicht: true,
          },
          {
            id: 'maengel',
            typ: 'maengelblock',
            label: 'Festgestellte Mängel',
            kostenschaetzung: true,
          },
          { id: 'fotos', typ: 'fotos', label: 'Fotos', max: 10 },
        ],
      },
      {
        id: 'ergebnis',
        titel: 'Abnahmeergebnis',
        felder: [
          {
            id: 'abnahme',
            typ: 'auswahl',
            label: 'Abnahme',
            optionen: ['erfolgt', 'erfolgt unter Vorbehalt', 'verweigert'],
            pflicht: true,
          },
          {
            id: 'vorbehalt',
            typ: 'mehrzeilig',
            label: 'Vorbehalte',
            zeilen: 3,
            pflicht: true,
            sichtbarWenn: { feld: 'abnahme', operator: 'ungleich', wert: 'erfolgt' },
          },
          { id: 'nachbesserungBis', typ: 'datum', label: 'Nachbesserung bis', breite: 'halb' },
          {
            id: 'gewaehrleistungBis',
            typ: 'datum',
            label: 'Gewährleistung bis',
            breite: 'halb',
          },
          {
            id: 'hinweisVerjaehrung',
            typ: 'hinweistext',
            text: 'Mit der Abnahme beginnt die Gewährleistungsfrist und die Beweislast geht auf den Auftraggeber über.',
            variante: 'warnung',
          },
        ],
      },
      unterschriftenAbschnitt(['Auftraggeber', 'Auftragnehmer']),
    ],
  }),
};

export const SYSTEM_TEMPLATES: readonly SystemTemplate[] = [EINZUG, AUSZUG, GEWERBE, ABNAHME];

export const systemTemplateNach = (schluessel: string): SystemTemplate | undefined =>
  SYSTEM_TEMPLATES.find((template) => template.schluessel === schluessel);

export { STANDARD_LAYOUT_CSS, STANDARD_LAYOUT_HTML };
